import { FC, useState, useEffect, useMemo } from 'react';
import { Button } from 'components/core';
import { FilterModalProps, Styled } from '.';
import { ProductAggregationValue, SortDirection } from 'types';
import { AppliedFacetFilter } from 'components/FacetFilters';
import { DsoModal } from 'components/DSO/DsoModal';
import { useI18n } from 'contexts/i18n';

interface CategoryOption {
  id: string;
  label: string;
  count: number;
}

interface SortOption {
  id: SortDirection;
  label: string;
}

const FilterModal: FC<FilterModalProps> = ({
  isOpen,
  onClose,
  aggregations,
  appliedFacetFilters,
  sortDirection,
  onApplyFilters,
}) => {
  // Local state for pending changes
  const [pendingFilters, setPendingFilters] =
    useState<AppliedFacetFilter[]>(appliedFacetFilters);
  const [pendingSort, setPendingSort] = useState<SortDirection | ''>(
    sortDirection
  );
  const i18n = useI18n();
  // Sync with props when modal opens
  useEffect(() => {
    if (isOpen) {
      setPendingFilters([...appliedFacetFilters]);
      setPendingSort(sortDirection);
    }
  }, [isOpen, appliedFacetFilters, sortDirection]);
  // Extract categories from aggregations
  const categories: CategoryOption[] = useMemo(() => {
    if (!aggregations || aggregations.length === 0) return [];

    const categoryAggregation = aggregations.find(
      agg => agg.key === 'category' || agg.name === 'Categories'
    );
    if (!categoryAggregation || !categoryAggregation.values) return [];

    return categoryAggregation.values.map((value: ProductAggregationValue) => ({
      id: value.name,
      label: value.name,
      count: value.count || 0,
    }));
  }, [aggregations]);

  // Sort options based on SortDirection enum
  const sortOptions: SortOption[] = [
    { id: SortDirection.AlphaAsc, label: 'Alphabetically: A-Z' },
    { id: SortDirection.AlphaDesc, label: 'Alphabetically: Z-A' },
    { id: SortDirection.PriceAsc, label: 'Price: Low to High' },
    { id: SortDirection.PriceDesc, label: 'Price: High to Low' },
    { id: SortDirection.NewestFirst, label: 'Newest First' },
    { id: SortDirection.OldestFirst, label: 'Oldest First' },
  ];

  // Count selected categories
  const selectedCategoriesCount = useMemo(() => {
    return pendingFilters.filter(filter =>
      categories.some(cat => cat.id === filter.filterValue)
    ).length;
  }, [pendingFilters, categories]);

  const handleCategoryChange = (categoryId: string, checked: boolean) => {
    const categoryAggregation = aggregations?.find(
      agg => agg.key === 'category' || agg.name === 'Categories'
    );
    if (!categoryAggregation) return;

    if (checked) {
      setPendingFilters([
        ...pendingFilters,
        {
          filterKey: categoryAggregation.key,
          filterValue: categoryId,
        },
      ]);
    } else {
      setPendingFilters(
        pendingFilters.filter(
          filter =>
            !(
              filter.filterKey === categoryAggregation.key &&
              filter.filterValue === categoryId
            )
        )
      );
    }
  };

  const handleClearCategories = () => {
    const categoryAggregation = aggregations?.find(
      agg => agg.key === 'category' || agg.name === 'Categories'
    );
    if (categoryAggregation) {
      setPendingFilters(
        pendingFilters.filter(
          filter => !categories.some(cat => cat.id === filter.filterValue)
        )
      );
    }
  };

  const handleClearSort = () => {
    setPendingSort('' as SortDirection);
  };

  const handleSortChange = (sortValue: SortDirection) => {
    setPendingSort(sortValue);
  };

  const handleClearAll = () => {
    handleClearCategories();
    handleClearSort();
  };

  const handleApplyFilters = () => {
    onApplyFilters(pendingFilters, pendingSort);
    onClose();
  };

  const isCategoryChecked = (categoryId: string) => {
    return pendingFilters.some(filter => filter.filterValue === categoryId);
  };

  return (
    <DsoModal
      isOpen={isOpen}
      onClose={onClose}
      width="800px"
      title="Filter by:"
      showCloseButton
      footer={
        <Styled.BottomActions>
          <Button variant="secondary" onClick={handleClearAll}>
            {i18n['Clear All']}
          </Button>
          <Button onClick={handleApplyFilters}>{i18n['See Results']}</Button>
        </Styled.BottomActions>
      }
    >
      <Styled.ModalContent>
        {/* Categories Section */}
        <Styled.Section>
          <Styled.SectionHeader>
            <Styled.SectionTitleWrapper>
              <Styled.SectionTitle>{i18n['Categories']}</Styled.SectionTitle>
              {selectedCategoriesCount > 0 && (
                <Styled.CountBadge>{selectedCategoriesCount}</Styled.CountBadge>
              )}
            </Styled.SectionTitleWrapper>
            {selectedCategoriesCount > 0 && (
              <Styled.ClearLink onClick={handleClearCategories}>
                {i18n['Clear']}
              </Styled.ClearLink>
            )}
          </Styled.SectionHeader>

          <Styled.CheckboxGrid>
            {categories.map(category => (
              <Styled.CheckboxWrapper key={category.id}>
                <Styled.CheckboxInput
                  type="checkbox"
                  id={`category-${category.id}`}
                  checked={isCategoryChecked(category.id)}
                  onChange={e =>
                    handleCategoryChange(category.id, e.target.checked)
                  }
                />
                <Styled.CheckboxLabel htmlFor={`category-${category.id}`}>
                  {category.label} ({category.count})
                </Styled.CheckboxLabel>
              </Styled.CheckboxWrapper>
            ))}
          </Styled.CheckboxGrid>
        </Styled.Section>

        {/* Sort by Section */}
        <Styled.Section>
          <Styled.SectionHeader>
            <Styled.SectionTitleWrapper>
              <Styled.SectionTitle>Sort by</Styled.SectionTitle>
            </Styled.SectionTitleWrapper>
            {pendingSort && (
              <Styled.ClearLink onClick={handleClearSort}>
                {i18n['Clear']}
              </Styled.ClearLink>
            )}
          </Styled.SectionHeader>

          <Styled.RadioGrid>
            {sortOptions.map(option => (
              <Styled.RadioWrapper key={option.id}>
                <Styled.RadioInput
                  type="radio"
                  id={`sort-${option.id}`}
                  name="sort"
                  value={option.id}
                  checked={pendingSort === option.id}
                  onChange={() => handleSortChange(option.id)}
                />
                <Styled.RadioLabel htmlFor={`sort-${option.id}`}>
                  {option.label}
                </Styled.RadioLabel>
              </Styled.RadioWrapper>
            ))}
          </Styled.RadioGrid>
        </Styled.Section>
      </Styled.ModalContent>
    </DsoModal>
  );
};

export default FilterModal;
